import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { OptionChangedEvent } from 'devextreme/ui/accordion';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	delay,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	ReplaySubject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from 'src/app/core/dx-types';
import { GdIcons } from 'src/app/ui/app-gd.icons';

import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { goToGdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	bulkDeleteFail,
	bulkDeleteSuccess,
	constructEntityList,
	createDatablocksFromEntitiesFail,
	createDatablocksFromEntitiesSuccess,
	deleteEntityFail,
	deleteEntitySuccess,
	EntitySelector,
	navigateToCreateFromEntity,
	navigateToEntity,
	navigateToEntityCreate,
	navigateToEntityDuplicate,
	refreshEntityList,
	stopPersistence,
	toggleEntityStatus,
	updateEntitiesFrameStatus,
} from '../../store';
import {
	EntityAction,
	EntityActionPopupComponent,
	IEntityActionModalData,
} from '../entity-action-popup/entity-action-popup.component';
import {
	EntityItemEntity,
	ParentProject,
	PersistenceState,
} from './entity-item.entity';
import { EntityListEntity } from './entity-list.entity';

type OptionChangeAction = 'page-change' | 'pageSize-change' | 'sort' | 'filter';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

type EntityOverlayDetails = {
	persistenceEntityId: number | undefined;
};

@Component({
	selector: 'app-entity-list',
	templateUrl: './entity-list.component.html',
	styleUrls: ['./entity-list.component.scss'],
	inputs: ['projectLabel'],
})
export class EntityListComponent extends DcBaseComponent implements OnInit {
	protected readonly GdIcons = GdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@entity.list.action.duplicate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.duplicateEntity(entity.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.toggleActivationStatus(entity);
			},
			icon: ActionIcons.ActivateElement,
		},
	];
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@entity.list.action.duplicate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.duplicateEntity(entity.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.toggleActivationStatus(entity);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];

	public isPersistedHeaderFilter = [
		{
			text: $localize`:i18n=@@entity.list.column.persistance.persisted:`,
			value: true,
		},
		{
			text: $localize`:i18n=@@entity.list.column.persistance.unpersisted:`,
			value: false,
		},
	];

	public isImportedHeaderFilter = [
		{
			text: $localize`:i18n=@@entity.list.column.imported:`,
			value: ['isImported', '=', true],
		},
		{
			text: $localize`:i18n=@@entity.list.column.unimported:`,
			value: ['isImported', '=', false],
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public projectHeaderFilter: Array<DxHeaderFilterItem> = [];

	public vo$: Observable<{
		isLoading: boolean;
		isFirstLoad: boolean;
		noDataText: string;
		list: EntityListEntity;
		openedOverlays: EntityOverlayDetails;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly entityListSubject = new ReplaySubject<EntityListEntity>(1);

	private readonly loadTriggerSubject = new BehaviorSubject<boolean>(true);

	private readonly openedOverlaySubject =
		new BehaviorSubject<EntityOverlayDetails>({
			persistenceEntityId: undefined,
		});

	private readonly optionChangedSubject = new ReplaySubject<OptionChangeAction>(
		1
	);
	private readonly refreshSubject = new ReplaySubject<void>(1);

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	public constructor(
		private readonly store: Store,
		private readonly entitySelector: EntitySelector,
		private readonly dxStorage: DxListsStorage,
		private readonly modalService: AppModalService,
		private readonly actions$: Actions,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'entity-list';
		this.dxLocalStorageKey = DcDxGrids.EntityListStandalone;

		this.vo$ = combineAll({
			isLoading: this.entitySelector.getLoading$(),
			noDataText: this.entitySelector
				.getLoading$()
				.pipe(
					map((v) =>
						v
							? $localize`:i18n=@@entity.list.dataLoading:`
							: $localize`:i18n=@@entity.list.noData:`
					)
				),
			isFirstLoad: this.loadTriggerSubject,
			list: this.entityListSubject,
			openedOverlays: this.openedOverlaySubject,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(constructEntityList());

		this.handleSubscriptions();

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@entity.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.entitySelector
			.getEntityList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				filter((l) => l.availableColumns.size !== 0),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.projectHeaderFilter = l
						.extractAvailableProjects()
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.entityListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();

		// Triggers loading after importing project for the first time
		this.entityListSubject
			.pipe(
				takeUntil(this.optionChangedSubject),
				switchMap((list) =>
					combineAll({
						list,
						count: timer(2000),
					})
				),
				tap(({ list }) => {
					const firstEl = list.elements.first();
					if (firstEl?.persistenceState.isDefault) {
						this.refreshSubject.next();
					}
				})
			)
			.subscribe();

		merge(
			this.optionChangedSubject,
			this.refreshSubject.pipe(
				delay(550),
				map(() => 'refresh')
			)
		)
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(520),
				filter(() => this.grid !== null),
				tap((val) => {
					const items = this.grid?.instance
						?.getDataSource()
						?.items() as Array<EntityItemEntity>;
					const entitesIds = items?.map((it) => it.id) ?? [];
					this.store.dispatch(
						updateEntitiesFrameStatus({
							ids: entitesIds,
							shouldReload: val === 'refresh',
						})
					);
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.loadTriggerSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshEntityList());
		this.refreshSubject.next();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public onOptionChange(e: OptionChangedEvent): void {
		if (!this.grid) {
			return;
		}
		const action: string = e.fullName;
		if (action === 'paging.pageIndex') {
			this.optionChangedSubject.next('page-change');
			return;
		}

		if (action === 'paging.pageSize') {
			this.optionChangedSubject.next('pageSize-change');
			return;
		}

		if (action.includes('filterValue')) {
			this.optionChangedSubject.next('filter');
			return;
		}

		if (action.includes('sortOrder')) {
			this.optionChangedSubject.next('sort');
			return;
		}
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.loadTriggerSubject);
		const isLoadingInProgress = await firstValueFrom(
			this.entitySelector.getLoading$()
		);

		if (firstLoad && !isLoadingInProgress) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.loadTriggerSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public createNewEntity(): void {
		this.store.dispatch(navigateToEntityCreate());
	}

	public stopPersistence(event: Event, entity: EntityItemEntity): void {
		event.stopPropagation();
		if (entity.persistenceState.recentJobId === null) {
			return;
		}
		this.store.dispatch(
			stopPersistence({
				jobKey: entity.persistenceState.recentJobId,
			})
		);
	}

	public async openPersistencePopover(
		event: Event,
		entity: EntityItemEntity
	): Promise<void> {
		event.stopPropagation();
		const previous = await firstValueFrom(this.openedOverlaySubject);
		this.openedOverlaySubject.next({
			persistenceEntityId:
				previous.persistenceEntityId === entity.id ? undefined : entity.id,
		});
	}

	public closePersistencePopover(): void {
		this.openedOverlaySubject.next({
			persistenceEntityId: undefined,
		});
	}

	public persistEntity(entity: EntityItemEntity): void {
		this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.persist.header:`,
			[entity.id],
			entity.label,
			EntityAction.Persist
		);
	}

	public depersist(entity: EntityItemEntity): void {
		this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.depersist.header:`,
			[entity.id],
			entity.label,
			EntityAction.Depersist
		);
	}

	public rePersist(entity: EntityItemEntity): void {
		this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.repersist.header:`,
			[entity.id],
			entity.label,
			EntityAction.Repersist
		);
	}

	public navigateToEntity(e: EntityItemEntity): void {
		this.store.dispatch(
			navigateToEntity({
				id: e.id,
			})
		);
	}

	public exportEntity(id: number): void {}

	public deleteEntityRow(event: Event, entity: EntityItemEntity): void {
		event.stopPropagation();
		this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.delete.header:`,
			[entity.id],
			entity.label,
			EntityAction.Delete
		);
	}

	public createDBFromEntity(event: Event, id: number): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToCreateFromEntity({
				id,
			})
		);
	}

	public duplicateEntity(id: number, event?: Event): void {
		if (event) {
			event.stopPropagation();
		}
		this.store.dispatch(
			navigateToEntityDuplicate({
				id,
			})
		);
	}

	private async createDatablocksFromSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		return this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.createDatablock.header:`,
			selectedEntities.map((e) => e.id),
			'',
			EntityAction.CreateDatablock
		);
	}

	private async persistSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		return this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.persist.header:`,
			selectedEntities.map((e) => e.id),
			selectedEntities.length === 1 ? selectedEntities[0].label : '',
			EntityAction.Persist
		);
	}
	private async unpersistSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		return this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.depersist.header:`,
			selectedEntities.map((e) => e.id),
			selectedEntities.length === 1 ? selectedEntities[0].label : '',
			EntityAction.Depersist
		);
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		if (selectedEntities.length === 1) {
			return this.openActionPopup(
				$localize`:i18n=@@entity.action.popup.delete.header:`,
				[selectedEntities[0].id],
				selectedEntities[0].label,
				EntityAction.Delete
			);
		}
		return this.openActionPopup(
			$localize`:i18n=@@entity.action.popup.delete.header:`,
			selectedEntities.map((k) => k.id),
			'',
			EntityAction.Delete
		);
	}

	private openActionPopup(
		title: string,
		entityId: Array<number>,
		element: string,
		action: EntityAction
	): void {
		this.modalService.openPopup<IEntityActionModalData>(
			EntityActionPopupComponent,
			{
				title,
				subTitle: '',
				entityId,
				element,
				action,
			},
			{
				disableClose: true,
			}
		);
	}

	private handleSubscriptions(): void {
		this.actions$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					deleteEntitySuccess,
					bulkDeleteSuccess,
					createDatablocksFromEntitiesSuccess,
					deleteEntityFail,
					bulkDeleteFail,
					createDatablocksFromEntitiesFail
				),
				tap(() => this.refresh())
			)
			.subscribe();
	}

	public toggleActivationStatus(entity: EntityItemEntity): void {
		this.store.dispatch(
			toggleEntityStatus({
				entityId: entity.id,
			})
		);
	}

	public calculatePersistenceFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as PersistenceState;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { isPersisted } = entity;
			return isPersisted === !!value;
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateProjectFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as ParentProject;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { label } = entity;
			return !!label && checkStringEquality(label, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
			ActionIcons.PersistInDisk,
			ActionIcons.UnPersist,
			ActionIcons.CreateFromDatablock,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.createDatablocksFromSelected(),
				iconName: actionMap.get(ActionIcons.CreateFromDatablock)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.createDBFromEntity:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): Promise<void> => this.persistSelected(),
				iconName: actionMap.get(ActionIcons.PersistInDisk)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.diskPersistence:`,
				typed: 'plain-context-action',
			},
			{
				order: 3,
				command: (): Promise<void> => this.unpersistSelected(),
				iconName: actionMap.get(ActionIcons.UnPersist)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.action.depersistAll:`,
				typed: 'plain-context-action',
			},
			{
				order: 4,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
